import React, { useState, useEffect, useMemo } from 'react';
import { courses, getDomainCourses } from '../../data/courses';

const AdvancedVocabularyViewer = ({ courseId, week = 1 }) => {
  const [vocabulary, setVocabulary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState(courseId);
  const [selectedWeek, setSelectedWeek] = useState(week);
  const [viewMode, setViewMode] = useState('single'); // 'single', 'all-weeks', 'all-courses'
  const [sortBy, setSortBy] = useState('alphabetical'); // 'alphabetical', 'category', 'week'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [hoveredTerm, setHoveredTerm] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  // Get current course info
  const currentCourse = courses.find(c => c.id === selectedCourse);
  
  // Get related courses (same domain, different levels)
  const domainCourses = useMemo(() => {
    if (!currentCourse) return [];
    return courses.filter(c => c.domain === currentCourse.domain);
  }, [currentCourse]);

  // Load vocabulary data
  useEffect(() => {
    const loadVocabulary = async () => {
      setLoading(true);
      setError(null);
      const vocabData = {};

      try {
        if (viewMode === 'single') {
          // Load single week
          const response = await fetch(`/coursework/${selectedCourse}/week${selectedWeek}/vocabulary.json`);
          if (response.ok) {
            const data = await response.json();
            vocabData[`${selectedCourse}_week${selectedWeek}`] = {
              ...data,
              courseId: selectedCourse,
              week: selectedWeek,
              courseName: currentCourse?.title || selectedCourse
            };
          }
        } else if (viewMode === 'all-weeks') {
          // Load all weeks for current course
          const weekPromises = Array.from({ length: 12 }, (_, i) => i + 1).map(async (weekNum) => {
            try {
              const response = await fetch(`/coursework/${selectedCourse}/week${weekNum}/vocabulary.json`);
              if (response.ok) {
                const data = await response.json();
                return {
                  key: `${selectedCourse}_week${weekNum}`,
                  data: {
                    ...data,
                    courseId: selectedCourse,
                    week: weekNum,
                    courseName: currentCourse?.title || selectedCourse
                  }
                };
              }
            } catch (e) {
              return null;
            }
          });
          
          const results = await Promise.all(weekPromises);
          results.filter(Boolean).forEach(({ key, data }) => {
            vocabData[key] = data;
          });
        } else if (viewMode === 'all-courses') {
          // Load vocabulary from all domain courses
          const coursePromises = domainCourses.map(async (course) => {
            const weekPromises = Array.from({ length: 12 }, (_, i) => i + 1).map(async (weekNum) => {
              try {
                const response = await fetch(`/coursework/${course.id}/week${weekNum}/vocabulary.json`);
                if (response.ok) {
                  const data = await response.json();
                  return {
                    key: `${course.id}_week${weekNum}`,
                    data: {
                      ...data,
                      courseId: course.id,
                      week: weekNum,
                      courseName: course.title
                    }
                  };
                }
              } catch (e) {
                return null;
              }
            });
            
            return Promise.all(weekPromises);
          });
          
          const allResults = await Promise.all(coursePromises);
          allResults.flat().filter(Boolean).forEach(({ key, data }) => {
            vocabData[key] = data;
          });
        }

        setVocabulary(vocabData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (selectedCourse) {
      loadVocabulary();
    }
  }, [selectedCourse, selectedWeek, viewMode, domainCourses, currentCourse]);

  // Process and filter all terms
  const processedTerms = useMemo(() => {
    const allTerms = [];
    
    Object.entries(vocabulary).forEach(([key, vocabData]) => {
      if (vocabData.terms) {
        vocabData.terms.forEach(term => {
          allTerms.push({
            ...term,
            sourceKey: key,
            courseId: vocabData.courseId,
            courseName: vocabData.courseName,
            week: vocabData.week
          });
        });
      }
    });

    // Filter terms
    let filteredTerms = allTerms.filter(term => {
      const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           term.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    // Sort terms
    filteredTerms.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'alphabetical':
          comparison = a.term.localeCompare(b.term);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category) || a.term.localeCompare(b.term);
          break;
        case 'week':
          comparison = a.week - b.week || a.term.localeCompare(b.term);
          break;
        case 'course':
          comparison = a.courseName.localeCompare(b.courseName) || a.week - b.week || a.term.localeCompare(b.term);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filteredTerms;
  }, [vocabulary, searchTerm, selectedCategory, sortBy, sortOrder]);

  // Get unique categories from all loaded vocabulary
  const allCategories = useMemo(() => {
    const categories = new Set();
    processedTerms.forEach(term => categories.add(term.category));
    return Array.from(categories).sort();
  }, [processedTerms]);

  const handleTermHover = (event, term) => {
    setHoveredTerm(term);
    setHoverPosition({ x: event.clientX, y: event.clientY });
  };

  const handleTermLeave = () => {
    setHoveredTerm(null);
  };

  const downloadVocabulary = (format) => {
    if (processedTerms.length === 0) return;
    
    let content, filename, mimeType;
    const timestamp = new Date().toISOString().split('T')[0];
    
    switch (format) {
      case 'json':
        content = JSON.stringify({
          exported_at: new Date().toISOString(),
          view_mode: viewMode,
          terms: processedTerms
        }, null, 2);
        filename = `vocabulary_export_${timestamp}.json`;
        mimeType = 'application/json';
        break;
      case 'csv':
        const headers = 'Term,Definition,Category,Course,Week,Example\n';
        const csvContent = processedTerms.map(term => 
          `"${term.term}","${term.definition}","${term.category}","${term.courseName}","Week ${term.week}","${term.example || ''}"`
        ).join('\n');
        content = headers + csvContent;
        filename = `vocabulary_export_${timestamp}.csv`;
        mimeType = 'text/csv';
        break;
      case 'txt':
        content = processedTerms.map(term => 
          `${term.term}\n${term.definition}\nCourse: ${term.courseName} - Week ${term.week}\nCategory: ${term.category}\n${term.example ? `Example: ${term.example}` : ''}\n${'='.repeat(50)}\n`
        ).join('\n');
        filename = `vocabulary_export_${timestamp}.txt`;
        mimeType = 'text/plain';
        break;
      default:
        return;
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || processedTerms.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">📝</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Vocabulary Available</h3>
          <p className="text-gray-500">{error || 'No vocabulary found for the selected criteria.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">📚 Advanced Vocabulary Viewer</h2>
            <p className="text-sm text-gray-600">
              Showing {processedTerms.length} terms • {viewMode === 'single' ? `${currentCourse?.title} - Week ${selectedWeek}` : 
                viewMode === 'all-weeks' ? `${currentCourse?.title} - All Weeks` : `All ${currentCourse?.domain.toUpperCase()} Courses`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {['json', 'csv', 'txt'].map(format => (
              <button
                key={format}
                onClick={() => downloadVocabulary(format)}
                className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                ↓ {format.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* View Mode Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="single">Single Week</option>
              <option value="all-weeks">All Weeks (Current Course)</option>
              <option value="all-courses">All Courses ({currentCourse?.domain.toUpperCase()})</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              {domainCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {viewMode === 'single' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Week</label>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(weekNum => (
                  <option key={weekNum} value={weekNum}>Week {weekNum}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Filtering and Sorting Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Terms</label>
            <input
              type="text"
              placeholder="Search vocabulary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">All Categories</option>
              {allCategories.map(category => (
                <option key={category} value={category}>
                  {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="alphabetical">Alphabetical</option>
              <option value="category">Category</option>
              <option value="week">Week</option>
              {viewMode === 'all-courses' && <option value="course">Course</option>}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Terms List */}
      <div className="p-4 sm:p-6">
        <div className="space-y-4">
          {processedTerms.map((term, index) => (
            <div key={`${term.sourceKey}-${index}`} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{term.term}</h3>
                <div className="flex flex-wrap gap-1">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {term.category.replace('_', ' ')}
                  </span>
                  {viewMode !== 'single' && (
                    <>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Week {term.week}
                      </span>
                      {viewMode === 'all-courses' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          {term.courseId.toUpperCase()}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              <p className="text-gray-700 mb-2 leading-relaxed">{term.definition}</p>
              
              {term.example && (
                <div className="bg-gray-50 border-l-4 border-blue-400 p-3 rounded-r-md">
                  <p className="text-sm text-gray-600">
                    <strong>Example:</strong> {term.example}
                  </p>
                </div>
              )}
              
              {viewMode === 'all-courses' && (
                <div className="mt-2 text-xs text-gray-500">
                  From: {term.courseName}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Floating Definition Tooltip */}
      {hoveredTerm && (
        <div 
          className="fixed z-50 max-w-sm bg-gray-900 text-white text-sm rounded-lg p-3 shadow-xl pointer-events-none"
          style={{ 
            left: `${Math.min(hoverPosition.x + 10, window.innerWidth - 200)}px`, 
            top: `${Math.max(hoverPosition.y - 10, 10)}px`,
            maxWidth: window.innerWidth < 768 ? '90vw' : '20rem'
          }}
        >
          <div className="font-semibold text-blue-300 mb-1">
            {hoveredTerm.term}
          </div>
          <div className="text-gray-200 text-xs leading-relaxed">
            {hoveredTerm.definition}
          </div>
          <div className="text-gray-400 text-xs mt-1 italic">
            {hoveredTerm.courseName} - Week {hoveredTerm.week}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedVocabularyViewer;